import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { AuthStore } from "./auth.store";

/**
 * Runs after the Okta callback succeeds but before any /admin route
 * renders. Verifies the user has at least one group that grants access
 * to this application. If the user authenticated with Okta but has no
 * matching entitlement, send them to /access-denied where they get an
 * actionable UI — previously we called store.logout() inline, which
 * bounced through Okta /logout and back, frequently looping on the
 * callback spinner when the IdP session was still live.
 */
const VALID_ROLES = [
  "admin",
  "finance",
  "clinical",
  "operational focal",
  "clinical focal point",
] as const;

async function hasEntitlement(store: AuthStore): Promise<boolean> {
  if (!store.isAuthenticated()) {
    await store.init();
  }
  return VALID_ROLES.some((r) => store.hasRole(r));
}

export const entitlementGuard: CanActivateFn = async () => {
  const store = inject(AuthStore);
  const router = inject(Router);

  if (await hasEntitlement(store)) return true;
  return router.createUrlTree(["/access-denied"]);
};

/**
 * Inverse of entitlementGuard. Protects /access-denied so an entitled
 * user who pastes the URL doesn't get stranded with only a Sign-out
 * button. Bounces them back into /admin.
 */
export const accessDeniedGuard: CanActivateFn = async () => {
  const store = inject(AuthStore);
  const router = inject(Router);

  if (await hasEntitlement(store)) {
    return router.createUrlTree(["/admin"]);
  }
  return true;
};
